/**
 * Trạng thái của phiên đối soát
 * Map với ReconciliationSessionResponseStatusEnum từ API
 */
export enum ReconciliationStatus {
  WAITING = "WAITING", // Chờ xử lý
  SOLVING = "SOLVING", // Đang xử lý
  SUCCESS = "SUCCESS", // Hoàn thành thành công
  FAILED = "FAILED", // Thất bại
}

/**
 * Kênh đối soát
 */
export enum ReconciliationChannel {
  TCB = "TCB", // Techcombank
}

/**
 * Phương thức đối soát
 */
export enum ReconciliationMethod {
  DIFF_INFO = "DIFF_INFO", // Khác thông tin
  DIFF_MONEY = "DIFF_MONEY", // Khác số tiền
  DIFF_STATUS = "DIFF_STATUS", // Khác trạng thái
  RECONCILIATION_EXIST = "RECONCILIATION_EXIST", // Đã đối soát
  NOT_RECONCILIATION = "NOT_RECONCILIATION", // Chưa đối soát
  RECONCILIATION_OTHER = "RECONCILIATION_OTHER", // Đối soát khác
  RECONCILIATION_DONE = "RECONCILIATION_DONE", // Hoàn thành đối soát
}

/**
 * Trạng thái giao dịch đối soát
 */
export enum ReconciliationTransactionStatus {
  WAITING = "WAITING", // Chờ xử lý
  SOLVED = "SOLVED", // Đã giải quyết
  SUCCESS = "SUCCESS", // Thành công
  FAILED = "FAILED", // Thất bại
}

/**
 * Model chính cho 1 phiên đối soát
 * Map với ReconciliationSessionResponse từ API
 */
export interface ReconciliationSessionItemModel {
  // ===== Identifiers =====
  id: string; // sessionId từ API

  // ===== Thông tin phiên đối soát =====
  channel: ReconciliationChannel; // channel từ API
  date: string; // date từ API (ngày đối soát)

  // ===== Dữ liệu thống kê =====
  totalTransactions: number; // totalTransactions từ API
  totalMismatchedTransactions: number; // totalMismatchedTransactions từ API

  // ===== Metadata =====
  status: ReconciliationStatus; // status từ API
}

/**
 * Model cho chi tiết phiên đối soát
 * Map với ReconciliationSessionDetailResponse từ API
 */
export interface ReconciliationSessionDetailModel
  extends ReconciliationSessionItemModel {
  totalMatchedTransactions: number; // totalMatchedTransactions từ API (chỉ có trong detail)
}

/**
 * Model cho giao dịch trong phiên đối soát
 * Map với ReconciliationResponse từ API
 */
export interface ReconciliationTransactionModel {
  id: string; // id từ API
  sessionId: string; // sessionId từ API
  txnNumber: string; // txnNumber từ API
  amount: number; // amount từ API (VND)
  diffAmount: number; // diffAmount từ API (VND) - số tiền chênh lệch

  // ===== Thông tin đối soát =====
  method: ReconciliationMethod; // method từ API
  status: ReconciliationTransactionStatus; // status từ API
  reconciliationDate: string; // reconciliationDate từ API
  appConfirmed: boolean; // appConfirmed từ API

  // ===== Giao dịch app =====
  appReconTransaction?: ReconciliationTransactionDetailModel;

  // ===== Giao dịch bank =====
  bankReconTransaction?: ReconciliationTransactionDetailModel;

  // ===== Thông tin hiển thị (computed fields) =====
  funTransId?: string; // Số bút toán từ API
  transactionType?: ReconciliationTransactionType; // Loại giao dịch từ API
}

/**
 * Loại giao dịch đối soát
 * Map với ReconciliationResponseTransactionTypeEnum từ API
 */
export enum ReconciliationTransactionType {
  PAYMENT = "PAYMENT", // Thanh toán
  REFUND = "REFUND", // Hoàn tiền
}

/**
 * Model chi tiết giao dịch đối soát
 * Map với ReconciliationTransaction từ API
 */
export interface ReconciliationTransactionDetailModel {
  txnNumber?: string; // txnNumber từ API
  referenceId?: string; // referenceId từ API
  refundTransactionLogId?: string; // refundTransactionLogId từ API
  amount?: number; // amount từ API
  paymentType?: string; // paymentType từ API (VIET_QR, ATM_CARD, BANKING)
  status?: string; // status từ API (NONE, CREATED, SUCCESS, CANCELED, FAIL, TIMEOUT, PENDING, REFUNDED)
  completeTime?: string; // completeTime từ API
  description?: string; // description từ API
  isUpdate?: boolean; // isUpdate từ API
  isDelete?: boolean; // isDelete từ API
}

/**
 * Model cho Pageable response (dùng chung)
 */
export interface PageableModel<T> {
  items: T[]; // Danh sách items
  pageNumber: number; // Page hiện tại (0-based)
  pageSize: number; // Số items trên 1 page
  totalSize: number; // Tổng số items
  totalPage: number; // Tổng số pages
}

/**
 * Request params cho getListReconciliationSessions
 * Map với getReconciliationSessions từ API
 */
export interface GetListReconciliationSessionsParams {
  page?: number; // Page number (0-based, default: 0)
  size?: number; // Page size (default: 10)
  sort?: string[]; // Sort criteria ["date,desc"]
  fromDate?: string; // Filter: từ ngày
  toDate?: string; // Filter: đến ngày
  channels?: ReconciliationChannel[]; // Filter: theo kênh
  statuses?: ReconciliationStatus[]; // Filter: theo trạng thái
}

/**
 * Request params cho getReconciliationSessionTransactions
 * Map với getReconciliationSessionTransactions từ API
 */
export interface GetReconciliationSessionTransactionsParams {
  sessionId: string; // ID của session
  equal: boolean; // Filter: chỉ lấy giao dịch khớp/không khớp
  page?: number; // Page number (0-based, default: 0)
  size?: number; // Page size (default: 10)
  sort?: string[]; // Sort criteria
}

/**
 * Request params cho exportReconciliationSessions
 * Map với exportReconciliationSessions từ API
 */
export interface ExportReconciliationSessionsParams {
  fromDate?: string; // Filter: từ ngày
  toDate?: string; // Filter: đến ngày
  channels?: ReconciliationChannel[]; // Filter: theo kênh
  statuses?: ReconciliationStatus[]; // Filter: theo trạng thái
  sort?: string[]; // Sort criteria
}

/**
 * Request params cho exportReconciliationSessionTransactions
 * Map với exportReconciliationSessionTransactions từ API
 */
export interface ExportReconciliationSessionTransactionsParams {
  sessionId: string; // ID của session
  equal?: boolean; // Filter: chỉ lấy giao dịch khớp/không khớp (optional cho export)
  sort?: string[]; // Sort criteria
}
